import type { FeatureMetadataRow } from './feature-metadata.models';
import type { TopFactor } from './ship.models';
import type {
  VoyageContributingFactorRow,
  VoyageContributingFactorsApiData,
  VoyageContributingFactorsVoyage,
  VoyageContributingPerCaptainNamed,
  VoyageContributingPerCaptainScalar,
} from './voyage-contributing-factors.models';
import { contributingFactorRowByFeatureId } from './voyage-contributing-factors.models';

/** Voyage-level risk after recalculation (matches inference-style casing). */
export interface RecalculatedVoyageRiskPrediction {
  VoyageLevelRisk: number;
  VoyageLevelRiskCategory: string | null;
}

/** One factor row returned from POST `/api/recalculation`. */
export interface RecalculatedFactorPayload {
  feature_id: number;
  feature: string;
  feature_value_ui: unknown;
  shap_value_ui: unknown;
  feature_value: number;
  apiName: string;
  dbName: string;
}

export interface RecalculatedVoyagePayload {
  DimVoyageId: string;
  baseValue?: number;
  captainWeightDict?: Readonly<Record<string, number>>;
  /** Positional map `{ "1": DimCaptainId, ... }` returned by inference after recalc. */
  captainCrewIdDict?: Readonly<Record<string, number>>;
  recalculatedVoyageRiskPrediction?: RecalculatedVoyageRiskPrediction | null;
  recalculatedFactors?: RecalculatedFactorPayload[] | null;
}

export interface RecalculatedShipRiskPrediction {
  ShipLevelRisk: number | null;
  ShipLevelRiskCategory: string | null;
}

export interface RecalculationShipDetailsData {
  DimShipId: string | number;
  Fleet: string;
  recalculatedShipRiskPrediction?: RecalculatedShipRiskPrediction | null;
  /** Rank key `"1"` | `"2"` | `"3"` → `feature_id`. */
  top3ContributingFactors?: Record<string, number> | null;
}

/** Model metadata returned alongside POST `/api/recalculation` results. */
export interface RecalculationModelDetails {
  version: number | string;
}

export interface RecalculationResponseData {
  voyages: RecalculatedVoyagePayload[];
  shipDetails: RecalculationShipDetailsData | null;
  modelDetails?: RecalculationModelDetails | null;
}

/** Wrapper for POST `/api/recalculation` success body. */
export interface RecalculationApiResponse {
  success: boolean;
  message: string;
  data: RecalculationResponseData;
}

/** `data` payload for GET `/api/inference-about` (proxied inference `/about`). */
export interface InferenceAboutData {
  modelDetails?: RecalculationModelDetails | null;
}

/** Wrapper for GET `/api/inference-about` success body. */
export interface InferenceAboutResponse {
  success: boolean;
  message: string;
  data: InferenceAboutData;
}

function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function mergeRecalculatedFactorOntoRow(
  row: VoyageContributingFactorRow,
  incoming: RecalculatedFactorPayload,
): void {
  const fui = incoming.feature_value_ui;
  const sui = incoming.shap_value_ui;
  const fv = incoming.feature_value;

  if (typeof fv === 'number' && Number.isFinite(fv)) {
    row.aggregateValue = fv;
  }

  if (row.shape === 'scalar') {
    // Normal scalar update: incoming value is a primitive.
    if (fui == null || typeof fui === 'number' || typeof fui === 'string') {
      if (fui != null) {
        row.scalarValueUi = String(fui);
      }
      const sh = toFiniteNumber(sui);
      if (sh !== null) {
        row.aggregateShap = sh;
      }
      return;
    }

    // The recalculation API returned per-captain dict data for a factor that was originally
    // classified as scalar (happens for NO_CAPTAIN_ASSIGNED voyages where the DB had no captain
    // data at sync time → empty perCaptain dict → classifyShape falls back to "scalar").
    // Upgrade the row to perCaptainScalar so the values are stored and rendered correctly.
    if (typeof fui === 'object' && !Array.isArray(fui)) {
      const fMap = fui as Record<string, unknown>;
      const sMap =
        typeof sui === 'object' && sui !== null && !Array.isArray(sui)
          ? (sui as Record<string, unknown>)
          : null;
      const next: Record<string, VoyageContributingPerCaptainScalar> = {};
      for (const capId of Object.keys(fMap)) {
        const v = toFiniteNumber(fMap[capId]);
        if (v === null) {
          continue;
        }
        const shap = toFiniteNumber(sMap?.[capId]) ?? 0;
        next[capId] = { value: v, shap };
      }
      if (Object.keys(next).length > 0) {
        row.shape = 'perCaptainScalar';
        row.perCaptain = next;
        // Derive aggregateShap as the mean of per-captain SHAPs so risk-% displays
        // while captainWeightDict is being applied by the SHAP scalar helper.
        const shapVals = Object.values(next).map((e) => e.shap);
        row.aggregateShap = shapVals.reduce((a, b) => a + b, 0) / shapVals.length;
      }
      return;
    }
    return;
  }

  const sMap =
    typeof sui === 'object' && sui !== null && !Array.isArray(sui)
      ? (sui as Record<string, unknown>)
      : null;

  if (typeof fui === 'object' && fui !== null && !Array.isArray(fui)) {
    const fMap = fui as Record<string, unknown>;

    if (row.shape === 'perCaptainNamed') {
      const next: Record<string, VoyageContributingPerCaptainNamed> = {};
      for (const capId of Object.keys(fMap)) {
        const entry = fMap[capId];
        const shap = toFiniteNumber(sMap?.[capId]) ?? 0;
        if (entry && typeof entry === 'object' && 'name' in entry && 'value' in entry) {
          const e = entry as { name: string; value: number };
          next[capId] = { name: String(e.name), value: e.value, shap };
        }
      }
      if (Object.keys(next).length > 0) {
        row.perCaptain = next;
      }
      return;
    }

    if (row.shape === 'perCaptainScalar') {
      const next: Record<string, VoyageContributingPerCaptainScalar> = {};
      for (const capId of Object.keys(fMap)) {
        const v = toFiniteNumber(fMap[capId]);
        if (v === null) {
          continue;
        }
        const shap = toFiniteNumber(sMap?.[capId]) ?? 0;
        next[capId] = { value: v, shap };
      }
      if (Object.keys(next).length > 0) {
        row.perCaptain = next;
      }
    }
    return;
  }

  if (fui != null && (typeof fui === 'number' || typeof fui === 'string')) {
    row.scalarValueUi = String(fui);
  }
  if (sMap && row.perCaptain && typeof row.perCaptain === 'object') {
    for (const capId of Object.keys(sMap)) {
      const sh = toFiniteNumber(sMap[capId]);
      const cur = row.perCaptain[capId];
      if (cur && typeof cur === 'object' && 'value' in cur && sh !== null) {
        (cur as VoyageContributingPerCaptainScalar).shap = sh;
      }
    }
  }
}

/**
 * Deep-merge recalculation `voyages[]` into the existing contributing-factors payload
 * (voyage risk + factor rows keyed by `DimVoyageId` / `feature_id`).
 */
export function mergeRecalculationIntoContributingData(
  data: VoyageContributingFactorsApiData,
  recalcVoyages: RecalculatedVoyagePayload[],
): VoyageContributingFactorsApiData {
  if (!recalcVoyages?.length) {
    return data;
  }
  const clone = structuredClone(data) as VoyageContributingFactorsApiData;
  const byDim = new Map(recalcVoyages.map((v) => [String(v.DimVoyageId), v]));

  for (const voyage of clone.voyages) {
    const rv = byDim.get(String(voyage.voyageId));
    if (!rv) {
      continue;
    }
    if (rv.baseValue != null && Number.isFinite(rv.baseValue)) {
      voyage.baseValue = rv.baseValue;
    }
    if (rv.captainWeightDict && Object.keys(rv.captainWeightDict).length > 0) {
      voyage.captainWeightDict = { ...rv.captainWeightDict };
    }
    if (rv.captainCrewIdDict && Object.keys(rv.captainCrewIdDict).length > 0) {
      voyage.captainCrewIdDict = { ...rv.captainCrewIdDict };
    }
    const pred = rv.recalculatedVoyageRiskPrediction;
    if (pred && Number.isFinite(pred.VoyageLevelRisk)) {
      voyage.voyageLevelRisk = pred.VoyageLevelRisk;
      voyage.voyageLevelRiskCategory = pred.VoyageLevelRiskCategory;
    }
    const factors = rv.recalculatedFactors;
    if (!factors?.length) {
      continue;
    }
    for (const rf of factors) {
      const row = contributingFactorRowByFeatureId(voyage, rf.feature_id);
      if (row) {
        mergeRecalculatedFactorOntoRow(row, rf);
      }
    }
  }
  return clone;
}

/** Build `ShipListItem.topFactors` from recalculation `top3ContributingFactors` + feature catalogue. */
export function topFactorsFromRecalculationTop3(
  top3: Record<string, number> | null | undefined,
  meta: readonly FeatureMetadataRow[],
): TopFactor[] {
  if (!top3 || typeof top3 !== 'object') {
    return [];
  }
  const byId = new Map(meta.map((r) => [r.featureId, r]));
  const order = ['1', '2', '3'] as const;
  const out: TopFactor[] = [];
  let rank = 1;
  for (const k of order) {
    const fid = top3[k];
    if (fid == null || !Number.isFinite(Number(fid))) {
      continue;
    }
    const id = Number(fid);
    const row = byId.get(id);
    out.push({
      rank,
      feature: row?.apiName ?? String(id),
      totalContribution: 0,
      uiName: row?.uiName ?? `Feature ${id}`,
    });
    rank += 1;
  }
  return out;
}
